import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { fetchUserOrdersThunk } from "../store/slices/ordersSlice";
import { Link } from "react-router-dom";

const ORDER_STEPS = [
  "pending",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
] as const;

const getStatusClass = (status: string) => {
  if (status === "delivered") return "bg-green-100 text-green-700";
  if (status === "pending") return "bg-yellow-100 text-yellow-700";
  if (status === "cancelled") return "bg-red-100 text-red-700";
  return "bg-blue-100 text-blue-700";
};

const Orders: React.FC = () => {
  const dispatch = useDispatch();
  const { orders, isLoading, error } = useSelector(
    (state: RootState) => state.orders
  );
  const user = useSelector((state: RootState) => state.auth.user);
  const { currentTenant } = useSelector((state: RootState) => state.tenant);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      dispatch(
        fetchUserOrdersThunk({
          tenantId: currentTenant.id,
          userId: user.id,
        }) as any
      );
    }
  }, [dispatch, user, currentTenant]);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          My Orders
        </h1>
      </div>
      {isLoading && (
        <div className="text-center py-12 text-green-600 font-semibold">
          Loading orders...
        </div>
      )}
      {error && (
        <div className="text-center py-12 text-red-600 font-semibold">
          {error}
        </div>
      )}
      {!isLoading && !error && orders.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          You have not placed any orders yet.
        </div>
      )}
      {!isLoading && !error && orders.length > 0 && (
        <div className="space-y-4">
          {orders.map((order) => {
            const currentStepIndex = ORDER_STEPS.findIndex(
              (step) => step === order.status
            );
            return (
              <div
                key={order.id}
                className="bg-white rounded-xl shadow border border-gray-100 p-4"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Order Number</p>
                    <p className="font-mono text-sm text-gray-900">
                      {order.order_number}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(order.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${getStatusClass(
                        order.status
                      )}`}
                    >
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                    <span className="font-semibold text-gray-900">
                      ₦{order.total_amount.toLocaleString()}
                    </span>
                    <Link
                      to={`/app/orders/${order.id}`}
                      className="text-green-600 hover:underline text-sm font-medium"
                    >
                      Track Order
                    </Link>
                  </div>
                </div>

                {order.status !== "cancelled" && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-2">
                      <span>Placed</span>
                      <span>Confirmed</span>
                      <span>Processing</span>
                      <span>Shipped</span>
                      <span>Delivered</span>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {ORDER_STEPS.map((step, index) => (
                        <div
                          key={step}
                          className={`h-2 rounded-full ${
                            index <= currentStepIndex
                              ? "bg-green-500"
                              : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
